// src/components/NotificationContainer.tsx
import React, { useEffect } from 'react';

interface NotificationProps {
  id: string;
  type: 'error' | 'neutral' | 'success';
  message: string;
  timeout?: number;
  onClose: () => void;
}

const Error: React.FC<NotificationProps> = ({ message, onClose, timeout }) => {
  useEffect(() => {
    if (timeout) {
      const timer = setTimeout(onClose, timeout);
      return () => clearTimeout(timer);
    }
  }, [timeout, onClose]);

  return (
    <div className="bg-[#ff4c4c] text-white px-5 py-2.5 rounded">
      <div className='flex items-center'>
        <span>{message}</span>
        <button className="filter brightness-[10000%]" onClick={onClose}>✖</button>
      </div>
    </div>
  );
};

const Neutral: React.FC<NotificationProps> = ({ message, onClose, timeout }) => {
  useEffect(() => {
    if (timeout) {
      const timer = setTimeout(onClose, timeout);
      return () => clearTimeout(timer);
    }
  }, [timeout, onClose]);

  return (
    <div className="bg-[#43a69f] text-white px-5 py-2.5 rounded">
      <div className='flex items-center'>
        <span>{message}</span>
        <button className="filter brightness-[10000%]" onClick={onClose}>✖</button>
      </div>
    </div>
  );
};

const Success: React.FC<NotificationProps> = ({ message, onClose, timeout }) => {
  useEffect(() => {
    if (timeout) {
      const timer = setTimeout(onClose, timeout);
      return () => clearTimeout(timer);
    }
  }, [timeout, onClose]);

  return (
    <div className="bg-[#94d4bc] text-white px-5 py-2.5 rounded">
      <div className='flex items-center'>
        <span>{message}</span>
        <button className="filter brightness-[10000%]" onClick={onClose}>✖</button>
      </div>
    </div>
  );
};

interface NotificationContainerProps {
  notifications: {
    id: string;
    type: 'error' | 'neutral' | 'success';
    message: string;
    timeout?: number;
  }[];
  removeNotification: (id: string) => void;
}

export const NotificationContainer: React.FC<NotificationContainerProps> = ({
  notifications,
  removeNotification,
}) => (
  <div className="fixed top-5 left-1/2 transform -translate-x-1/2 px-5 py-2.5 rounded z-[9999] flex flex-col gap-2 justify-between items-center">
      {notifications.map((n) => {
        const props = {
          key: n.id,
          id: n.id,
          type: n.type,
          message: n.message,
          timeout: n.timeout,
          onClose: () => removeNotification(n.id),
        };

        switch (n.type) {
          case 'error':
            return <Error {...props} />;
          case 'neutral':
            return <Neutral {...props} />;
          case 'success':
            return <Success {...props} />;
          default:
            return null;
        }
      })}
  </div>
);

// import { useNotifications } from '../context/NotificationContext';
// const { addNotification } = useNotifications();
// <button onClick={() => addNotification('success', 'Gelukt!', 3000)}>